import React, { lazy, ReactNode, useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import {
  BlankLayout,
  ClientViewLayout,
  ClientWebsiteCreationLayout,
  DefaultLayout,
  MessageServiceLayout,
  ResellerServiceLayout,
  MCQTestLayout,
  UserProfileLayout,
} from '../Components/Layouts';
import { RoutePIM } from '../Interfaces/PIMs';
import { AccessDenied, LandingPage } from '../Pages';
import { Error } from '../Components/Pages';
import { MainLoader } from '../Components/Common';
import { useDispatch } from 'react-redux';
import { HttpStatusCode } from 'axios';
import { setRoutes } from '../Storage/Redux/slice/routesSlice';
import { TblModuleStore, TblRouteStore } from '../Interfaces/DataBase';
import {
  Emailverification,
  ExternalLogin,
  ForgetPassword,
  Login,
  ResetPassword,
} from '../Pages/Auth';
import { SD_Services } from '../Utility/StaticDatas';
import { useGetClientRoutesQuery } from '../Apis/Client/SPClientRoutesApi';
import { TblPage } from '../Interfaces/DataBase/Client';
import { convertData, rtkErrorRead } from '../Helper';
import { useModulePermissionHub, useRoutePermissionHub } from '../Connections';
import { ModuleContext } from '../Context';
import StylesWrapper from './StylesWrapper';
import { getAuthToken, removeAuthToken } from '../Helper/authToken';
const Routes = () => {
  const dispatch = useDispatch();
  const [router, setRouter] = useState<any>(null);
  const token = getAuthToken();
  const routeHub = useRoutePermissionHub(token);
  const moduleHub = useModulePermissionHub(token);
  const clientRoutes = useGetClientRoutesQuery(null, {
    skip: !token,
  });
  const [dbRouteList, setDbRouteList] = useState<TblRouteStore[]>([]);
  const [dbModuleList, setDbModuleList] = useState<TblModuleStore[]>([]);
  const [dbClientRoute, setDbClientRoute] = useState<TblPage[]>([]);

  useEffect(() => {
    if (!routeHub.isLoading) {
      if (routeHub.isError) {
        rtkErrorRead(routeHub.error);
        const pathname: string[] = [
          '/auth/login',
          '/auth/resetpassword',
          '/auth/forgetpassword',
          '/auth/emailverification',
          '/auth/whatsapp-login',
        ];

        if (
          'status' in routeHub.error! &&
          !pathname.some((obj) => window.location.pathname.includes(obj))
        ) {
          if (routeHub.error?.status === HttpStatusCode.Unauthorized) {
            removeAuthToken();
            window.location.replace('/auth/login');
          }
        }
      }
      setDbRouteList(routeHub.data! || []);
    }
  }, [routeHub.isLoading, routeHub.data, routeHub.error, routeHub.isError]);

  useEffect(() => {
    if (!moduleHub.isLoading) {
      if (moduleHub.isError) {
        rtkErrorRead(moduleHub.error);
        if ('status' in moduleHub.error!) {
          if (moduleHub.error?.status === HttpStatusCode.Unauthorized) {
            removeAuthToken();
            window.location.replace('/auth/login');
          }
        }
      }
      setDbModuleList(moduleHub.data! || []);
    }
  }, [moduleHub.isLoading, moduleHub.data, moduleHub.error, moduleHub.isError]);

  useEffect(() => {
    if (!clientRoutes.isLoading) {
      if (clientRoutes.isError) {
        if ('status' in clientRoutes.error!) {
          if (clientRoutes.error?.status === HttpStatusCode.Unauthorized) {
            removeAuthToken();
            window.location.replace('/auth/login');
          }
        }
      }
      setDbClientRoute(convertData(clientRoutes.data?.result) || []);
    }
  }, [
    clientRoutes.isLoading,
    clientRoutes.data,
    clientRoutes.isError,
    clientRoutes.error,
  ]);

  useEffect(() => {
    const initializeRoutes = async () => {
      const staticRoutes: RoutePIM[] = [
        {
          path: '/auth/login',
          exact: true,
          element: <Login />,
          layout: 'blank',
          title: 'Login',
        },
        {
          path: '/auth/exrternal-login/:id',
          exact: true,
          element: <ExternalLogin />,
          layout: 'blank',
          title: 'Login',
        },
        {
          path: '/auth/resetpassword/:code',
          exact: true,
          element: <ResetPassword />,
          layout: 'blank',
          title: 'Reset Password',
        },
        {
          path: '/auth/forgetpassword',
          exact: true,
          element: <ForgetPassword />,
          layout: 'blank',
          title: 'Forget Password',
        },
        {
          path: '/auth/emailverification/:userId/:codeid',
          exact: true,
          element: <Emailverification />,
          layout: 'blank',
          title: 'Email Verification',
        },
        {
          path: '/',
          element: <LandingPage />,
          title: 'LandingPage',
          service: SD_Services.LMS_Service,
        },
        {
          path: '/accessDenied',
          element: <AccessDenied />,
          title: 'Access Denied',
          service: SD_Services.LMS_Service,
        },
        {
          path: '*',
          element: <Error />,
          title: 'Error',
          service: SD_Services.LMS_Service,
        },
      ];

      if (dbRouteList.length !== 0) {
        dispatch(setRoutes(dbRouteList));
        dbRouteList.forEach((obj) => {
          const service =
            obj.service === SD_Services.LMS_Service
              ? 'LMSService'
              : obj.service === SD_Services.Client_App
                ? 'Client'
                : obj.service === SD_Services.MessageService
                  ? 'Messages'
                  : obj.service === SD_Services.ResellerService
                    ? 'ResellerService'
                  : obj.service === SD_Services.MCQTestService
                    ? 'LMSService'
                    : 'Profile';
          const Component = lazy(
            () => import(`../Pages/${service}/${obj.routeComponent!}`)
          );
          staticRoutes.push({
            path: obj.routePath!,
            title: obj.routeName!,
            exact: obj.routeExact!,
            service: obj.service!,
            element: (
              <ModuleContext.Provider value={dbModuleList}>
                <Component />
              </ModuleContext.Provider>
            ),
          });
        });
      }

      if (dbClientRoute.length !== 0) {
        dbClientRoute.forEach((obj) => {
          const Component = lazy(() => import('../Pages/Client/PreviewPage'));
          staticRoutes.push({
            path: obj.PageUrl!,
            layout: 'clientView',
            element: <Component />,
          });
        });
      }

      const getElement = (route: RoutePIM): ReactNode => {
        if (route.layout === 'blank') {
          return (
            <StylesWrapper>
              <BlankLayout>{route.element}</BlankLayout>
            </StylesWrapper>
          );
        } else if (route.layout === 'clientView') {
          return <ClientViewLayout>{route.element}</ClientViewLayout>;
        } else {
          if (route.service === SD_Services.LMS_Service) {
            return (
              <StylesWrapper>
                <DefaultLayout>{route.element}</DefaultLayout>
              </StylesWrapper>
            );
          } else if (route.service === SD_Services.Client_App) {
            return (
              <StylesWrapper>
                <ClientWebsiteCreationLayout>
                  {route.element}
                </ClientWebsiteCreationLayout>
              </StylesWrapper>
            );
          } else if (route.service === SD_Services.MessageService) {
            return (
              <StylesWrapper>
                <MessageServiceLayout>{route.element}</MessageServiceLayout>
              </StylesWrapper>
            );
          } else if (route.service === SD_Services.ResellerService) {
            return (
              <StylesWrapper>
                <ResellerServiceLayout>{route.element}</ResellerServiceLayout>
              </StylesWrapper>
            );
          } else if (route.service === SD_Services.MCQTestService) {
            return (
              <StylesWrapper>
                <MCQTestLayout>{route.element}</MCQTestLayout>
              </StylesWrapper>
            );
          } else if (route.service === SD_Services.Profile) {
            return (
              <StylesWrapper>
                <UserProfileLayout>{route.layout}</UserProfileLayout>
              </StylesWrapper>
            );
          }
        }
      };

      const finalRoutes = staticRoutes.map((route) => ({
        ...route,
        element: getElement(route),
      }));

      setRouter(createBrowserRouter(finalRoutes));
    };

    initializeRoutes();
  }, [dbRouteList, dbClientRoute]);

  if (
    routeHub.isLoading ||
    clientRoutes.isLoading ||
    !router ||
    (token && !dbRouteList)
  )
    return <MainLoader />;

  return <RouterProvider router={router} />;
};

export default Routes;
